import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WASTE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GapBadge } from "@/components/waste/GapBadge";
import { ProjectDetail } from "@/components/waste/ProjectDetail";
import { InboxTab } from "@/components/waste/InboxTab";
import { SubcontractorsTab } from "@/components/waste/SubcontractorsTab";
import { Recycle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

function ProjectsTab({ projects, readonly, reload }) {
  const [sel, setSel] = useState(null);
  const active = sel || projects.find((p) => p.report_status === "issued")?.contract_id || projects[0]?.contract_id;
  if (!projects.length) return <Card className="border-dashed border-sage-light p-8 text-center text-sm text-muted-foreground">{readonly ? "No issued waste reports yet." : "No projects yet."}</Card>;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
      <div className="space-y-2">
        {projects.map((p) => (
          <button key={p.contract_id} onClick={() => setSel(p.contract_id)} data-testid={WASTE.projectItem(p.contract_id)}
            className={`w-full rounded-xl border p-3 text-left transition-colors ${active === p.contract_id ? "border-forest-mid bg-sage-surface" : "border-sage-light bg-white hover:bg-sage-surface/60"}`}>
            <p className="text-sm font-semibold text-forest-dark">{p.name}</p>
            <p className="text-[11px] text-muted-foreground">{p.status === "completed" ? "Completed" : "Ongoing"} · {p.report_count} report(s){p.takeover ? " · taken over" : ""}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {!readonly && <GapBadge status={p.gap.status} />}
              {p.report_status === "issued" && <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"><BadgeCheck className="h-3 w-3" /> Issued</span>}
            </div>
          </button>
        ))}
      </div>
      {active && <ProjectDetail key={active} cid={active} readonly={readonly} onChanged={reload} />}
    </div>
  );
}

export default function WastePage() {
  const { hasRole } = useAuth();
  const readonly = !hasRole("tenant_admin", "contract_manager");
  const [projects, setProjects] = useState([]);
  const load = useCallback(() => api.get("/waste-projects").then((r) => setProjects(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid={WASTE.page}>
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark sm:text-3xl"><Recycle className="h-6 w-6 text-forest-mid" /> Waste &amp; Subcontractors</h1>
        <p className="text-sm text-muted-foreground">{readonly ? "Issued Circular Waste & Carpet Upcycling reports." : "Subcontractor waste reports measured against Limelite's circular methodology and targets."}</p>
      </div>
      {readonly ? <ProjectsTab projects={projects} readonly reload={load} /> : (
        <Tabs defaultValue="projects">
          <TabsList>
            <TabsTrigger value="projects" data-testid={WASTE.tabProjects}>Projects</TabsTrigger>
            <TabsTrigger value="inbox" data-testid={WASTE.tabInbox}>Reports inbox</TabsTrigger>
            <TabsTrigger value="subs" data-testid={WASTE.tabSubs}>Subcontractors</TabsTrigger>
          </TabsList>
          <TabsContent value="projects" className="mt-4"><ProjectsTab projects={projects} reload={load} /></TabsContent>
          <TabsContent value="inbox" className="mt-4"><InboxTab projects={projects} /></TabsContent>
          <TabsContent value="subs" className="mt-4"><SubcontractorsTab projects={projects} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
